import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { exchangeRates } from '../api/endpoints.js';
import { type ExchangeRateDetail } from '../api/schemas.js';

export function ExchangeRateDetailPage(): React.JSX.Element {
  const id = useParams().exchangeRateId ?? '';
  const query = exchangeRates.useItem(id);
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = exchangeRates.useSoftDelete();

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.EXCHANGE_RATE_READ}
      parent={{ to: '/exchange-rates', label: 'Exchange rates' }}
      title={(data) => `${data.baseCurrency}/${data.quoteCurrency}`}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.EXCHANGE_RATE_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/exchange-rates/${id}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.EXCHANGE_RATE_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data: ExchangeRateDetail) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete exchange rate"
            description={`${data.baseCurrency}/${data.quoteCurrency} will be hidden from future conversions.`}
            confirmLabel="Delete"
            destructive
            loading={remove.isPending}
            onConfirm={() => {
              void remove.mutateAsync(id).then(() => {
                setConfirmingDelete(false);
                void navigate('/exchange-rates');
              });
            }}
          />
          <DetailGrid>
            <DetailField label="Base currency">{data.baseCurrency}</DetailField>
            <DetailField label="Quote currency">{data.quoteCurrency}</DetailField>
            <DetailField label="Rate">{data.rate}</DetailField>
            <DetailField label="Source">{data.source}</DetailField>
            <DetailField label="Effective">{data.effectiveAt}</DetailField>
            <DetailField label="Retrieved">{data.retrievedAt}</DetailField>
            <DetailField label="Expires">{data.expiresAt}</DetailField>
            <DetailField label="Version">{data.version}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
